/**
 * sportsConfig.js — Single source of truth for all sports metadata.
 *
 * Usage:
 *   import { LAUNCH_SPORTS, HIDDEN_SPORTS, SPORT_META, SPORT_ID_MAP } from "../../config/sportsConfig";
 *
 * To add a new sport:
 *   1. Add an entry to SPORT_META with its id, slug, displayName and icon path.
 *   2. Add its name to LAUNCH_SPORTS (in display order) if it should appear in the nav.
 *   3. That's it — GameCard, SportsBanner, and competition pages all update automatically.
 *
 * Place this file at:  src/config/sportsConfig.js
 */

// ─── Ordered nav strip ────────────────────────────────────────────────────────
//
// Names must match the `name` field returned by get_sports_banner exactly.
// Sports not in this list that the API returns are appended automatically after
// the last entry here (before "More Sports").
//
// ⚠️  Use the exact name the get_sports_banner API returns as each array value.
// The backend CRM stores American Football as "American Football" (not "NFL").
// SportsBanner renders the displayName from SPORT_META, so users still see "NFL".
export const LAUNCH_SPORTS = ["Soccer", "Rugby League", "American Football", "Golf"];

// ─── Suppressed sports ────────────────────────────────────────────────────────
//
// API sports suppressed entirely — not shown even as coming-soon.
// Matched case-insensitively.
//
export const HIDDEN_SPORTS = ["NHL", "MLB", "Basketball"];

// ─── Sport metadata ───────────────────────────────────────────────────────────
//
// Keyed by the exact name returned by the get_sports_banner API.
// id      — backend sport_id (null until confirmed by backend)
// slug    — used in GameCard API routes: /api/{slug}/gamecard/match_teams
// icon    — path to the sport SVG, relative to src/assets/sports/
//           e.g. "nfl.svg" → src/assets/sports/nfl.svg
//           If your project serves icons from a different location, update
//           the icon values here — no other file needs changing.
//
export const SPORT_META = {
  "Soccer":            { id: 1,    slug: "soccer",          displayName: "Soccer",          icon: "soccer.svg" },
  "Cricket":           { id: 2,    slug: "cricket",         displayName: "Cricket",         icon: "cricket.svg" },
  // API name = "American Football" (CRM/backend value); renders as "NFL" in the UI.
  "American Football": { id: 3,    slug: "nfl",             displayName: "NFL",             icon: "nfl.svg" },
  "Australian Rules":  { id: 5,    slug: "australianrules", displayName: "AFL",             icon: "australianrules.svg" },
  "Baseball":          { id: 7,    slug: "baseball",        displayName: "Baseball",        icon: "baseball.svg" },
  "Basketball":        { id: 8,    slug: "basketball",      displayName: "Basketball",      icon: "basketball.svg" },
  "Esport":            { id: 11,   slug: "esport",          displayName: "Esport",          icon: "esport.svg" },
  "Gaelic Games":      { id: 12,   slug: "gaelicgames",     displayName: "Gaelic Games",    icon: "gaelicgames.svg" },
  "Ice Hockey":        { id: 16,   slug: "icehockey",       displayName: "Ice Hockey",      icon: "icehockey.svg" },
  "Rugby League":      { id: 18,   slug: "rugbyleague",     displayName: "Rugby League",    icon: "rugbyleague.svg" },
  "Golf":              { id: null, slug: "golf",            displayName: "Golf",            icon: "golf.svg" },
};

// ─── Derived lookups ─────────────────────────────────────────────────────────

/**
 * sport_id → slug map — drop-in replacement for the inline `sports` object in
 * GameCard.js and any other component that needs to build API route paths.
 *
 * Example:
 *   import { SPORT_ID_MAP } from "../../config/sportsConfig";
 *   const route = `/api/${SPORT_ID_MAP[sport_id]}/gamecard/match_teams`;
 */
export const SPORT_ID_MAP = Object.fromEntries(
  Object.values(SPORT_META)
    .filter((m) => m.id !== null)
    .map((m) => [m.id, m.slug])
);

/**
 * Get the icon filename for a given sport name.
 * Returns null if the sport is not in SPORT_META.
 *
 * Usage in a component that renders icons:
 *   const filename = getSportIcon(sport.name);  // e.g. "nfl.svg"
 *   // Then import or require from src/assets/sports/{filename}
 */
export function getSportIcon(name) {
  return SPORT_META[name]?.icon ?? null;
}

/**
 * Get the slug for a given sport_id.
 * Returns null if the id is not mapped.
 */
export function getSlugById(id) {
  return SPORT_ID_MAP[id] ?? null;
}
